/* ==========================================================================
   Satjeevan — Reels Section JS (sj-reels.js)
   Continuous right-to-left auto-scroll drift slider.
   Architecture mirrors sj-testimonials.js for consistency.
   ========================================================================== */

(function () {
  'use strict';

  /* ==========================================================================
     ★ CONFIGURATION — change 'on' to 'off' to disable auto-scroll entirely
     ========================================================================== */
  var AUTO_SCROLL = 'on'; // 'on' | 'off'
  /* ========================================================================== */

  var SCROLL_DELAY_MS = 3000; // ms to wait before auto-scroll begins
  var SPEED           = 28;   // px/s
  var GAP             = 16;   // px — must match gap value in sj-reels.css

  /**
   * Initialise every .sj-reel-viewport found on the page.
   */
  function initReels() {
    /** @type {NodeListOf<HTMLElement>} */
    var viewports = document.querySelectorAll('.sj-reel-viewport');

    viewports.forEach(function (viewport) {
      // Prevent double-init (Theme Editor live-reload safety)
      if (viewport.hasAttribute('data-sj-reel-init')) return;
      viewport.setAttribute('data-sj-reel-init', '1');

      var track = /** @type {HTMLElement} */ (viewport.querySelector('.sj-reel-track'));
      if (!track) return;

      /** @type {HTMLElement[]} */
      var cards = Array.prototype.slice.call(track.children);
      if (!cards.length) return;

      // Respect prefers-reduced-motion — no animation at all
      var mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (mq.matches) return;

      // If AUTO_SCROLL is off, skip starting the animation loop entirely.
      // Drag and swipe will still work because those are user-initiated.
      if (AUTO_SCROLL !== 'on') return;

      var cardWidth = 0;
      var setWidth  = 0;
      var trackX    = 0;

      // scrollingPaused covers hover/focus/drag.
      // delayActive covers the initial 3-second hold before scroll starts.
      // userScrolling covers an in-progress scroll gesture (see onWindowScroll).
      var scrollingPaused = false;
      var delayActive     = true;
      var userScrolling   = false;

      /** @type {number | null} */
      var lastTime = null;
      /** @type {number | null} */
      var rafId = null;
      /** @type {number | null} */
      var scrollResumeTimer = null;

      /* ------ measure --------------------------------------------------- */
      function measure() {
        if (!cards[0]) return;
        cardWidth = cards[0].getBoundingClientRect().width;
        // Track has two identical halves for seamless looping
        var uniqueCount = Math.round(cards.length / 2);
        setWidth = uniqueCount * (cardWidth + GAP);
      }

      /* ------ animation frame ------------------------------------------- */
      /**
       * @param {number} t
       */
      function frame(t) {
        if (lastTime === null) lastTime = t;
        var dt = Math.min((t - lastTime) / 1000, 0.05);
        lastTime = t;

        if (!scrollingPaused && !delayActive) {
          trackX -= SPEED * dt;
          if (trackX <= -setWidth) trackX += setWidth;
        }

        track.style.transform = 'translateX(' + trackX + 'px)';

        // Idle instead of endlessly rescheduling: while paused (hover/focus/
        // drag), still in the initial delay, or mid-scroll-gesture, nothing
        // changes visually from tick to tick, so stop the rAF chain rather
        // than keep waking the main thread up ~60x/second for no reason — a
        // running rAF loop competes with the browser's own scroll/compositing
        // work on every single frame, which is exactly what shows up as
        // stutter on mobile. ensureRunning() restarts the chain the moment
        // any of those conditions clears.
        if (scrollingPaused || delayActive || userScrolling) {
          rafId = null;
          return;
        }
        rafId = requestAnimationFrame(frame);
      }

      function ensureRunning() {
        if (rafId !== null || scrollingPaused || delayActive || userScrolling) return;
        lastTime = null;
        rafId = requestAnimationFrame(frame);
      }

      function onWindowScroll() {
        userScrolling = true;
        if (scrollResumeTimer) clearTimeout(scrollResumeTimer);
        scrollResumeTimer = setTimeout(function () {
          scrollResumeTimer = null;
          userScrolling = false;
          ensureRunning();
        }, 200);
      }

      /* ------ boot ------------------------------------------------------- */
      measure();
      window.addEventListener('resize', measure);
      window.addEventListener('scroll', onWindowScroll, { passive: true });

      // Start the rAF loop immediately so the track is positioned correctly,
      // but keep delayActive = true so no movement happens yet.
      rafId = requestAnimationFrame(frame);

      // Release auto-scroll after the configured delay
      setTimeout(function () {
        delayActive = false;
        ensureRunning();
      }, SCROLL_DELAY_MS);

      /* ------ hover pause ------------------------------------------------ */
      viewport.addEventListener('mouseenter', function () { scrollingPaused = true; });
      viewport.addEventListener('mouseleave', function () { scrollingPaused = false; ensureRunning(); });

      /* ------ focus pause (keyboard a11y) -------------------------------- */
      viewport.addEventListener('focusin',  function () { scrollingPaused = true; });
      viewport.addEventListener('focusout', function () { scrollingPaused = false; ensureRunning(); });

      /* ------ mouse drag ------------------------------------------------- */
      var isDragging = false;
      var startX     = 0;

      viewport.addEventListener('mousedown', function (e) {
        var me = /** @type {MouseEvent} */ (e);
        isDragging      = true;
        startX          = me.pageX;
        scrollingPaused = true;
      });

      window.addEventListener('mousemove', function (e) {
        var me = /** @type {MouseEvent} */ (e);
        if (!isDragging) return;
        var dx = me.pageX - startX;
        trackX += dx;
        startX  = me.pageX;
        if (trackX <= -setWidth) trackX += setWidth;
        if (trackX > 0)          trackX -= setWidth;
        track.style.transform = 'translateX(' + trackX + 'px)';
      });

      window.addEventListener('mouseup', function () {
        isDragging = false;
        // scrollingPaused is controlled by mouseenter/mouseleave
      });

      /* ------ touch swipe ------------------------------------------------ */
      viewport.addEventListener('touchstart', function (e) {
        var te = /** @type {TouchEvent} */ (e);
        if (!te.touches || !te.touches[0]) return;
        isDragging      = true;
        startX          = te.touches[0].pageX;
        scrollingPaused = true;
      }, { passive: true });

      window.addEventListener('touchmove', function (e) {
        var te = /** @type {TouchEvent} */ (e);
        if (!isDragging || !te.touches || !te.touches[0]) return;
        var dx = te.touches[0].pageX - startX;
        trackX += dx;
        startX  = te.touches[0].pageX;
        if (trackX <= -setWidth) trackX += setWidth;
        if (trackX > 0)          trackX -= setWidth;
        if (track) {
          track.style.transform = 'translateX(' + trackX + 'px)';
        }
      }, { passive: true });

      window.addEventListener('touchend', function () {
        isDragging      = false;
        scrollingPaused = false;
        ensureRunning();
      });

      /* ------ trackpad / horizontal wheel -------------------------------- */
      viewport.addEventListener('wheel', function (e) {
        var we = /** @type {WheelEvent} */ (e);
        if (Math.abs(we.deltaX) > Math.abs(we.deltaY)) {
          we.preventDefault();
          trackX -= we.deltaX;
          if (trackX <= -setWidth) trackX += setWidth;
          if (trackX > 0)          trackX -= setWidth;
          track.style.transform = 'translateX(' + trackX + 'px)';
        }
      }, { passive: false });
    });
  }

  /* ------ Bootstrap ------------------------------------------------------ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initReels();
      initReelVideos();
    });
  } else {
    initReels();
    initReelVideos();
  }

  /* ------ Shopify Theme Editor live-reload -------------------------------- */
  document.addEventListener('shopify:section:load', function (event) {
    var ev     = /** @type {CustomEvent} */ (event);
    var target = /** @type {HTMLElement} */ (ev.target);
    if (target && target.querySelector('.sj-reel-viewport')) {
      initReels();
    }
    if (target && target.querySelector('.sj-reel-card')) {
      initReelVideos();
    }
  });

  document.addEventListener('shopify:block:select', function () {
    initReels();
    initReelVideos();
  });

  /* ------ Video Play/Pause Logic ------------------------------------------ */

  // Each reel block renders TWICE (real card + an aria-hidden clone used for
  // the seamless infinite loop — see sj-reels.liquid), and every render has
  // autoplay set unconditionally. Left alone, every video on the page —
  // visible or not — decodes and plays at once, a heavy sustained cost on
  // mobile. One shared observer keeps only the video(s) actually in the
  // viewport playing; everything else stays paused until scrolled to.
  /** @type {IntersectionObserver | null} */
  var reelVideoObserver = null;

  function getReelVideoObserver() {
    if (reelVideoObserver || !('IntersectionObserver' in window)) return reelVideoObserver;
    reelVideoObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var vid = /** @type {HTMLVideoElement} */ (entry.target);
        if (entry.isIntersecting) {
          vid.play().catch(function () {});
        } else {
          vid.pause();
        }
      });
    }, { threshold: 0.15 });
    return reelVideoObserver;
  }

  function initReelVideos() {
    /** @type {NodeListOf<HTMLElement>} */
    var reelCards = document.querySelectorAll('.sj-reel-card');
    reelCards.forEach(function (card) {
      if (card.hasAttribute('data-sj-vid-init')) return;
      card.setAttribute('data-sj-vid-init', '1');

      /** @type {HTMLVideoElement | null} */
      var video = card.querySelector('video');
      /** @type {HTMLElement | null} */
      var btn = card.querySelector('.sj-reel-card__play-icon');
      if (!video || !btn) return;

      var observer = getReelVideoObserver();
      if (observer) observer.observe(video);

      /** @type {HTMLElement | null} */
      var iconPlay = btn.querySelector('.sj-icon-play');
      /** @type {HTMLElement | null} */
      var iconPause = btn.querySelector('.sj-icon-pause');

      function updateBtnState() {
        if (video && video.paused) {
          if (iconPlay) iconPlay.style.display = 'block';
          if (iconPause) iconPause.style.display = 'none';
        } else {
          if (iconPlay) iconPlay.style.display = 'none';
          if (iconPause) iconPause.style.display = 'block';
        }
      }

      updateBtnState();

      if (video) {
        video.addEventListener('play', updateBtnState);
        video.addEventListener('pause', updateBtnState);
      }

      // Toggle play/pause on click (if not dragging)
      var isClickDrag = false;
      card.addEventListener('mousedown', function () { isClickDrag = false; });
      card.addEventListener('mousemove', function () { isClickDrag = true; });
      card.addEventListener('mouseup', function (e) {
        if (!isClickDrag) {
          // If the clicked element is an anchor link (e.g. product link), don't intercept
          var target = /** @type {HTMLElement} */ (e.target);
          if (target.tagName.toLowerCase() === 'a' || target.closest('a')) return;
          
          e.preventDefault();
          if (video && video.paused) {
            video.play();
          } else if (video) {
            video.pause();
          }
        }
      });
    });
  }

})();
